import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Upload, Download, Eye, Trash2, FileText, Save, X as XIcon } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { useModal } from '../components/AppModal';
import useUserPreference from '../hooks/useUserPreference';
import useViewMode from '../hooks/useViewMode';
import useFieldLayout from '../hooks/useFieldLayout';
import VistaControles from '../components/VistaControles';
import DataTable from '../components/DataTable';

const Budgets = () => {
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const modal = useModal();
  const fileInputRef = useRef(null);

  const [items, setItems] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [search, setSearch] = useUserPreference('budgets:search', '');
  const [customerFilter, setCustomerFilter] = useUserPreference('budgets:customer', '');
  const [viewMode, setViewMode] = useViewMode('budgets');
  const fl = useFieldLayout('budgets', viewMode);
  const [uploadCustomerId, setUploadCustomerId] = useState('');
  const [detailItem, setDetailItem] = useState(null);
  const [detailForm, setDetailForm] = useState(null);
  const [saving, setSaving] = useState(false);

  const loadInitialData = useCallback(async () => {
    try {
      const [budgetsRes, customersRes, invoicesRes] = await Promise.all([
        api.get('/budgets'),
        api.get('/customers'),
        api.get('/invoices')
      ]);
      setItems(Array.isArray(budgetsRes.data) ? budgetsRes.data : []);
      setCustomers(Array.isArray(customersRes.data) ? customersRes.data : []);
      setInvoices(Array.isArray(invoicesRes.data) ? invoicesRes.data : []);
    } catch (error) {
      modal.error('Error cargando presupuestos');
    } finally {
      setLoading(false);
    }
  }, [modal]);

  useEffect(() => {
    loadInitialData();
  }, [loadInitialData]);

  const reloadBudgets = async () => {
    const res = await api.get('/budgets');
    setItems(Array.isArray(res.data) ? res.data : []);
  };

  const reloadBudgetsAndInvoices = async () => {
    const [budgetsRes, invoicesRes] = await Promise.all([
      api.get('/budgets'),
      api.get('/invoices')
    ]);
    setItems(Array.isArray(budgetsRes.data) ? budgetsRes.data : []);
    setInvoices(Array.isArray(invoicesRes.data) ? invoicesRes.data : []);
  };

  const handleFiles = async (fileList) => {
    if (!fileList || fileList.length === 0) {
      return;
    }

    const files = Array.from(fileList);
    const uploaded = [];
    const errors = [];

    setUploading(true);

    for (let index = 0; index < files.length; index += 1) {
      const file = files[index];
      setUploadProgress({ current: index + 1, total: files.length, fileName: file.name });

      try {
        const formData = new FormData();
        formData.append('files', file);
        if (uploadCustomerId !== '') {
          formData.append('customerId', uploadCustomerId);
        }

        const res = await api.post('/budgets/upload', formData);
        if (res.data[0]?.success) {
          uploaded.push(res.data[0].item);
        } else {
          errors.push(res.data[0]?.error || file.name);
        }
      } catch (error) {
        errors.push(error.response?.data?.message || file.name);
      }
    }

    if (uploaded.length > 0) {
      await reloadBudgets();
      modal.success(`${uploaded.length} presupuesto${uploaded.length > 1 ? 's' : ''} subido${uploaded.length > 1 ? 's' : ''}`);
    }

    if (errors.length > 0) {
      modal.error(errors.join('\n'));
    }

    setUploading(false);
    setUploadProgress(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const openBudgetFile = async (item, download = false) => {
    try {
      const res = await api.get(`/budgets/${item.id}/file`, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: item.mimeType || 'application/octet-stream' });
      const url = window.URL.createObjectURL(blob);

      if (download || !(item.mimeType || '').includes('pdf')) {
        const link = document.createElement('a');
        link.href = url;
        link.download = item.fileName;
        document.body.appendChild(link);
        link.click();
        link.remove();
      } else {
        window.open(url, '_blank', 'noopener,noreferrer');
      }

      window.setTimeout(() => window.URL.revokeObjectURL(url), 1000);
    } catch (error) {
      modal.error('Error abriendo el presupuesto');
    }
  };

  const handleDelete = (item) => {
    modal.confirm(
      `¿Eliminar "${item.fileName}"?`,
      async () => {
        try {
          await api.delete(`/budgets/${item.id}`);
          await reloadBudgets();
          if (detailItem?.id === item.id) {
            closeDetail();
          }
        } catch (error) {
          modal.error(error.response?.data?.message || 'Error eliminando presupuesto');
        }
      }
    );
  };

  const openDetail = (item) => {
    setDetailItem(item);
    setDetailForm({
      customerId: item.customerId ? String(item.customerId) : '',
      invoiceIds: item.invoices.map((invoice) => String(invoice.id)),
      notes: item.notes || ''
    });
  };

  const closeDetail = () => {
    setDetailItem(null);
    setDetailForm(null);
  };

  const handleSaveDetail = async () => {
    if (!detailItem || !detailForm) {
      return;
    }

    setSaving(true);
    try {
      const payload = {
        customerId: detailForm.customerId,
        invoiceIds: detailForm.invoiceIds,
        notes: detailForm.notes
      };
      const res = await api.put(`/budgets/${detailItem.id}`, payload);
      await reloadBudgetsAndInvoices();
      setDetailItem(res.data);
      setDetailForm({
        customerId: res.data.customerId ? String(res.data.customerId) : '',
        invoiceIds: res.data.invoices.map((invoice) => String(invoice.id)),
        notes: res.data.notes || ''
      });
    } catch (error) {
      modal.error(error.response?.data?.message || 'Error guardando presupuesto');
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (value) => (
    value
      ? new Date(value).toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' })
      : '—'
  );

  const formatDateTime = (value) => (
    value
      ? new Date(value).toLocaleString('es-ES', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        })
      : '—'
  );

  const formatSize = (bytes) => {
    if (!bytes) return '—';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const filtered = items.filter((item) => {
    const matchesSearch = search.trim() === ''
      || item.fileName.toLowerCase().includes(search.toLowerCase())
      || (item.customer?.name || '').toLowerCase().includes(search.toLowerCase())
      || item.invoices.some((invoice) => invoice.invoiceNumber.toLowerCase().includes(search.toLowerCase()));

    const matchesCustomer = customerFilter === '' || String(item.customerId || '') === customerFilter;
    return matchesSearch && matchesCustomer;
  });

  const linkedInvoiceCount = items.reduce((count, item) => count + item.invoices.length, 0);
  const selectedInvoiceIds = new Set(detailForm?.invoiceIds || []);
  const availableInvoices = detailItem
    ? invoices.filter((invoice) => {
        const isCurrentRelation = selectedInvoiceIds.has(String(invoice.id));
        const isFree = !invoice.budget || invoice.budget.id === detailItem.id;

        if (!isCurrentRelation && !isFree) {
          return false;
        }

        if (!detailForm?.customerId) {
          return true;
        }

        return String(invoice.customerId) === String(detailForm.customerId);
      })
    : [];

  const handleDetailCustomerChange = (customerId) => {
    if (!detailForm) {
      return;
    }

    if (customerId === '' && selectedInvoiceIds.size > 0) {
      modal.error('No puedes quitar el cliente mientras haya facturas relacionadas.');
      return;
    }

    if (customerId !== '') {
      const selectedInvoices = invoices.filter((invoice) => selectedInvoiceIds.has(String(invoice.id)));
      const hasMismatch = selectedInvoices.some((invoice) => String(invoice.customerId) !== String(customerId));
      if (hasMismatch) {
        modal.error('Quita primero las facturas de otro cliente antes de cambiar el cliente del presupuesto.');
        return;
      }
    }

    setDetailForm((current) => ({ ...current, customerId }));
  };

  const toggleInvoiceInDetail = (invoice) => {
    if (!detailForm) {
      return;
    }

    const invoiceId = String(invoice.id);
    const isSelected = selectedInvoiceIds.has(invoiceId);

    if (!isSelected && detailForm.customerId && String(invoice.customerId) !== String(detailForm.customerId)) {
      modal.error('La factura seleccionada pertenece a otro cliente.');
      return;
    }

    const nextInvoiceIds = isSelected
      ? detailForm.invoiceIds.filter((id) => id !== invoiceId)
      : [...detailForm.invoiceIds, invoiceId];

    setDetailForm((current) => ({
      ...current,
      customerId: !isSelected && !current.customerId ? String(invoice.customerId) : current.customerId,
      invoiceIds: nextInvoiceIds
    }));
  };

  const columns = [
    { key: 'file', header: 'Archivo', className: 'dt-strong', fixed: true, render: (item) => (
      <div>
        <div style={{ wordBreak: 'break-word' }}>{item.fileName}</div>
        <span className="dt-sub">{formatSize(item.fileSize)} · alta {formatDate(item.createdAt)}</span>
      </div>
    ) },
    { key: 'customer', header: 'Cliente', render: (item) => (
      <span style={{ fontWeight: 500 }}>{item.customer ? item.customer.name : 'Sin asociar'}</span>
    ) },
    { key: 'nif', header: 'NIF', render: (item) => item.customer?.nif || '—' },
    { key: 'invoices', header: 'Facturas', render: (item) => (
      item.invoices.length > 0 ? (
        <div className="item-card-tags">
          {item.invoices.map((invoice) => (
            <button
              key={invoice.id}
              type="button"
              className="badge badge-info"
              style={{ border: 'none', cursor: 'pointer' }}
              onClick={(event) => {
                event.stopPropagation();
                navigate(`/invoices/edit/${invoice.id}`);
              }}
            >
              {invoice.invoiceNumber}
            </button>
          ))}
        </div>
      ) : '—'
    ) },
    { key: 'state', header: 'Estado', render: (item) => (
      <span className={`badge ${item.customer ? 'badge-success' : 'badge-gray'}`}>
        {item.customer ? 'Con cliente' : 'Sin cliente'}
      </span>
    ) },
    { key: 'actions', header: '', className: 'col-actions', render: (item) => (
      <div className="dt-actions" onClick={(event) => event.stopPropagation()}>
        <button type="button" className="btn-icon" title="Abrir" onClick={() => openBudgetFile(item, false)}><Eye size={16} /></button>
        <button type="button" className="btn-icon" title="Descargar" onClick={() => openBudgetFile(item, true)}><Download size={16} /></button>
        {hasPermission('invoices', 'delete') && (
          <button type="button" className="btn-icon" title="Eliminar" style={{ color: '#ef4444' }} onClick={() => handleDelete(item)}><Trash2 size={16} /></button>
        )}
      </div>
    ) },
  ];

  const fields = fl.fields(columns);

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Presupuestos</h1>
          <p className="page-subtitle">
            {items.length} archivos · {items.filter((item) => item.customerId).length} con cliente · {linkedInvoiceCount} facturas relacionadas
          </p>
        </div>
      </div>

      {hasPermission('invoices', 'create') && (
        <div
          style={{
            border: `2px dashed ${dragOver ? 'var(--primary)' : 'var(--gray-300)'}`,
            borderRadius: 8,
            padding: 20,
            marginBottom: 20,
            background: dragOver ? 'var(--gray-100)' : 'transparent',
            transition: 'all 0.2s'
          }}
          onDragOver={(event) => {
            event.preventDefault();
            if (!uploading) setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(event) => {
            event.preventDefault();
            setDragOver(false);
            if (!uploading) {
              handleFiles(event.dataTransfer.files);
            }
          }}
        >
          <div className="form-row" style={{ alignItems: 'end', marginBottom: uploading ? 12 : 0 }}>
            <div className="form-group" style={{ flex: 1, minWidth: 240 }}>
              <label className="form-label">Cliente del presupuesto</label>
              <select
                className="form-input"
                value={uploadCustomerId}
                onChange={(event) => setUploadCustomerId(event.target.value)}
                disabled={uploading}
              >
                <option value="">Sin asociar todavía</option>
                {customers.map((customer) => (
                  <option key={customer.id} value={customer.id}>
                    {customer.name} {customer.nif ? `(${customer.nif})` : ''}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group" style={{ flex: '0 0 auto' }}>
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
              >
                <Upload size={18} />
                {uploading ? 'Subiendo...' : 'Buscar archivos'}
              </button>
            </div>
          </div>

          {uploading && uploadProgress ? (
            <div style={{ marginTop: 8 }}>
              <div style={{ fontSize: '0.85rem', color: 'var(--gray-500)', marginBottom: 8 }}>
                {uploadProgress.current} / {uploadProgress.total} · {uploadProgress.fileName}
              </div>
              <div style={{ background: 'var(--gray-200)', borderRadius: 999, height: 6 }}>
                <div
                  style={{
                    width: `${(uploadProgress.current / uploadProgress.total) * 100}%`,
                    height: '100%',
                    borderRadius: 999,
                    background: 'var(--primary)'
                  }}
                />
              </div>
            </div>
          ) : (
            <div style={{ textAlign: 'center', color: 'var(--gray-600)', paddingTop: 8 }}>
              <Upload size={36} style={{ color: 'var(--gray-400)', marginBottom: 8 }} />
              <p style={{ margin: 0 }}>Arrastra aquí presupuestos PDF u ODT, o usa el botón para seleccionarlos.</p>
              <p style={{ margin: '4px 0 0', fontSize: '0.8rem', color: 'var(--gray-400)' }}>
                El nombre inicial se toma del archivo y luego podrás relacionarlo con facturas.
              </p>
            </div>
          )}

          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,.odt"
            multiple
            style={{ display: 'none' }}
            onChange={(event) => handleFiles(event.target.files)}
          />
        </div>
      )}

      <div className="card">
        <div className="search-bar">
          <div className="search-input-wrapper">
            <Search />
            <input
              type="text"
              className="form-input"
              placeholder="Buscar por archivo, cliente o factura..."
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
          </div>

          <select
            className="form-input"
            style={{ width: 240 }}
            value={customerFilter}
            onChange={(event) => setCustomerFilter(event.target.value)}
          >
            <option value="">Todos los clientes</option>
            {customers.map((customer) => (
              <option key={customer.id} value={customer.id}>
                {customer.name}
              </option>
            ))}
          </select>

          <div style={{ marginLeft: 'auto' }}>
            <VistaControles
              viewMode={viewMode}
              onViewModeChange={setViewMode}
              fields={fields}
              hidden={fl.hidden}
              onToggleField={fl.toggle}
              onReorderFields={fl.reorder}
            />
          </div>
        </div>

        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--gray-500)' }}>Cargando...</div>
        ) : filtered.length === 0 ? (
          <div className="empty-state">
            <FileText size={64} />
            <h3>No hay presupuestos</h3>
            <p>Sube el primer presupuesto para empezar</p>
          </div>
        ) : viewMode === 'table' ? (
          <DataTable
            columns={fl.orderColumns(columns)}
            rows={filtered}
            hiddenKeys={fl.hidden}
            getRowProps={(item) => ({ onClick: () => openDetail(item), style: { cursor: 'pointer' } })}
          />
        ) : (
          <div className="items-grid">
            {filtered.map((item) => (
              <div
                key={item.id}
                className="item-card item-card--interactive"
                onClick={() => openDetail(item)}
              >
                <div className="item-card-header">
                  <div className="item-card-main">
                    <div className="item-card-avatar">
                      <FileText size={18} />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div className="item-card-title" style={{ wordBreak: 'break-word' }}>{item.fileName}</div>
                      <div className="item-card-subtitle">
                        {formatSize(item.fileSize)} · alta {formatDate(item.createdAt)}
                      </div>
                    </div>
                  </div>
                  {!fl.isHidden('state') && (
                    <span className={`badge ${item.customer ? 'badge-success' : 'badge-gray'}`}>
                      {item.customer ? 'Con cliente' : 'Sin cliente'}
                    </span>
                  )}
                </div>

                <div className="item-card-body">
                  {!fl.isHidden('customer') && (
                    <div className="item-card-row" style={fl.cardStyle('customer')}>
                      <span className="item-label">Cliente</span>
                      <span style={{ fontWeight: 500 }}>
                        {item.customer ? item.customer.name : 'Sin asociar'}
                      </span>
                    </div>
                  )}
                  {!fl.isHidden('nif') && item.customer?.nif && (
                    <div className="item-card-row" style={fl.cardStyle('nif')}>
                      <span className="item-label">NIF</span>
                      <span>{item.customer.nif}</span>
                    </div>
                  )}
                </div>

                {!fl.isHidden('invoices') && (
                  <div className="item-card-stack">
                    <span className="item-label">Facturas relacionadas</span>
                    {item.invoices.length > 0 ? (
                      <div className="item-card-tags">
                        {item.invoices.map((invoice) => (
                          <button
                            key={invoice.id}
                            type="button"
                            className="badge badge-info"
                            style={{ border: 'none', cursor: 'pointer' }}
                            onClick={(event) => {
                              event.stopPropagation();
                              navigate(`/invoices/edit/${invoice.id}`);
                            }}
                          >
                            {invoice.invoiceNumber}
                          </button>
                        ))}
                      </div>
                    ) : (
                      <div className="item-card-note">Sin facturas relacionadas todavía.</div>
                    )}
                  </div>
                )}

                <div className="item-card-footer">
                  <span className="item-card-date">Creado el {formatDate(item.createdAt)}</span>
                  <div className="item-card-actions" onClick={(event) => event.stopPropagation()}>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline"
                      title="Abrir"
                      onClick={() => openBudgetFile(item, false)}
                    >
                      <Eye size={14} />
                    </button>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline"
                      title="Descargar"
                      onClick={() => openBudgetFile(item, true)}
                    >
                      <Download size={14} />
                    </button>
                    {hasPermission('invoices', 'delete') && (
                      <button
                        type="button"
                        className="btn btn-sm btn-danger"
                        title="Eliminar"
                        onClick={() => handleDelete(item)}
                      >
                        <Trash2 size={14} />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {detailItem && detailForm && (
        <div className="modal-overlay" onClick={closeDetail}>
          <div className="app-modal app-modal--wide app-modal--form" onClick={(event) => event.stopPropagation()}>
            <button className="app-modal-close" onClick={closeDetail} aria-label="Cerrar">
              <XIcon size={18} />
            </button>

            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
              <FileText size={24} style={{ color: 'var(--primary)', flexShrink: 0 }} />
              <div style={{ minWidth: 0 }}>
                <h3 style={{ margin: 0, fontSize: '1rem', wordBreak: 'break-word' }}>{detailItem.fileName}</h3>
                <span style={{ fontSize: '0.75rem', color: 'var(--gray-500)' }}>
                  {formatSize(detailItem.fileSize)} · alta {formatDateTime(detailItem.createdAt)}
                </span>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Cliente</label>
                <select
                  className="form-input"
                  value={detailForm.customerId}
                  onChange={(event) => handleDetailCustomerChange(event.target.value)}
                  disabled={!hasPermission('invoices', 'edit') || saving}
                >
                  <option value="">Sin asociar</option>
                  {customers.map((customer) => (
                    <option key={customer.id} value={customer.id}>
                      {customer.name} {customer.nif ? `(${customer.nif})` : ''}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label className="form-label">Facturas relacionadas</label>
                <div
                  className="form-input"
                  style={{
                    minHeight: 44,
                    maxHeight: 220,
                    overflowY: 'auto',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'stretch',
                    gap: 8,
                    padding: 10
                  }}
                >
                  {availableInvoices.length > 0 ? (
                    availableInvoices.map((invoice) => {
                      const checked = selectedInvoiceIds.has(String(invoice.id));
                      return (
                        <label
                          key={invoice.id}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: 10,
                            cursor: hasPermission('invoices', 'edit') ? 'pointer' : 'default'
                          }}
                        >
                          <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <input
                              type="checkbox"
                              checked={checked}
                              disabled={!hasPermission('invoices', 'edit') || saving}
                              onChange={() => toggleInvoiceInDetail(invoice)}
                            />
                            <span>{invoice.invoiceNumber}</span>
                          </span>
                          <button
                            type="button"
                            className="badge badge-info"
                            style={{ border: 'none', cursor: 'pointer' }}
                            onClick={(event) => {
                              event.preventDefault();
                              event.stopPropagation();
                              navigate(`/invoices/edit/${invoice.id}`);
                            }}
                          >
                            Ver factura
                          </button>
                        </label>
                      );
                    })
                  ) : (
                    <span style={{ color: 'var(--gray-400)' }}>
                      {detailForm.customerId
                        ? 'No hay facturas disponibles para este cliente.'
                        : 'Selecciona un cliente o marca una factura para fijarlo automáticamente.'}
                    </span>
                  )}
                </div>
              </div>
            </div>

            <div className="form-group" style={{ marginBottom: 20 }}>
              <label className="form-label">Notas</label>
              <textarea
                className="form-input"
                value={detailForm.notes}
                onChange={(event) => setDetailForm((current) => ({ ...current, notes: event.target.value }))}
                disabled={!hasPermission('invoices', 'edit') || saving}
                placeholder="Notas internas del presupuesto"
              />
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <button type="button" className="btn btn-outline" onClick={() => openBudgetFile(detailItem, false)}>
                  <Eye size={16} /> Abrir archivo
                </button>
                <button type="button" className="btn btn-outline" onClick={() => openBudgetFile(detailItem, true)}>
                  <Download size={16} /> Descargar
                </button>
              </div>

              {hasPermission('invoices', 'edit') && (
                <button type="button" className="btn btn-primary" onClick={handleSaveDetail} disabled={saving}>
                  <Save size={16} /> {saving ? 'Guardando...' : 'Guardar'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Budgets;
